import { serverNow } from "@/utils/serverClock";

/**
 * Stops a click on an EXPIRED presigned S3 link from opening S3's raw error page.
 *
 * File links in API payloads are presigned S3 URLs, valid for an hour from signing
 * (AWS_QUERYSTRING_EXPIRE = 3600). The app does not refetch on window focus, so a tab
 * left open past the hour still shows the old links, and a click on "Pobierz" lands on
 * S3's "AccessDenied — Request has expired" XML instead of the file. The expiry is right;
 * showing that page to a user is not.
 *
 * Screens that re-sign the link at click time (ManagedFileLink, AttachmentLinks,
 * useManagedFileOpener) prevent the default before the click reaches this listener — it
 * sits on window, in the bubble phase, so they keep working. Every other plain
 * `<a href={file.url}>` in the app gets a message telling the user to refresh the page,
 * which fetches the data — and the links — anew.
 */

export const EXPIRED_FILE_LINK_MSG =
  "Sesja wygasła — strona była otwarta zbyt długo bez odświeżenia i link do pliku jest już nieważny. " +
  "Odśwież stronę i pobierz plik ponownie.";

// A link with a few seconds left would die on the way to S3.
const EXPIRY_MARGIN_MS = 10_000;

const AMZ_DATE = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;

/** When a presigned (SigV4) S3 link stops working, in epoch ms; null for any other URL. */
export function presignedUrlExpiresAt(href: string): number | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const signedAt = AMZ_DATE.exec(url.searchParams.get("X-Amz-Date") ?? "");
  const lifetime = Number(url.searchParams.get("X-Amz-Expires") ?? NaN);
  if (!signedAt || !Number.isFinite(lifetime)) return null;
  const [, y, mo, d, h, mi, s] = signedAt.map(Number);
  return Date.UTC(y, mo - 1, d, h, mi, s) + lifetime * 1000;
}

/** True only when the server's clock is known and says the link is (about to be) dead. */
export function isPresignedUrlExpired(href: string, now: number | null): boolean {
  if (now === null) return false;
  const expiresAt = presignedUrlExpiresAt(href);
  return expiresAt !== null && now >= expiresAt - EXPIRY_MARGIN_MS;
}

interface GuardOptions {
  now?: () => number | null;
  confirm?: (message: string) => boolean;
  reload?: () => void;
}

export function installExpiredFileLinkGuard(target: Window = window, opts: GuardOptions = {}): () => void {
  const now = opts.now ?? serverNow;
  const confirm = opts.confirm ?? ((message: string) => target.confirm(message));
  const reload = opts.reload ?? (() => target.location.reload());

  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented) return;
    // auxclick: only the middle button opens a link (in a new tab).
    if (e.type === "auxclick" && e.button !== 1) return;
    const anchor = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    // SVG <a> has no string href — not a download link anyway.
    if (!anchor || typeof anchor.href !== "string") return;
    if (!isPresignedUrlExpired(anchor.href, now())) return;
    e.preventDefault();
    if (confirm(`${EXPIRED_FILE_LINK_MSG}\n\nOdświeżyć stronę teraz?`)) reload();
  };

  target.addEventListener("click", onClick);
  target.addEventListener("auxclick", onClick);
  return () => {
    target.removeEventListener("click", onClick);
    target.removeEventListener("auxclick", onClick);
  };
}
